import {
  requireWorkflowPayloadVersion,
  type WorkflowDefinitionDocument,
  type WorkflowStepChildLink,
  type WorkflowTerminalOutcome,
  type WorkflowChildDefinitionReference,
} from './platform-workflow-definition-lifecycle.models';
import {
  initializeBlankStep,
  type StepAuthoringChildDefinition,
} from './workflow-step-composition';
import { isEqual } from 'lodash-es';
export const childCode = (value: unknown) =>
  typeof value === 'string' &&
  value.length <= 120 &&
  /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(value);
export const childReferenceIdentity = (
  reference: WorkflowChildDefinitionReference,
) =>
  JSON.stringify([
    reference.workflowCode,
    reference.definitionVersionCode,
    reference.contentHash,
  ]);
export const childRuleFields = [
  'version',
  'id',
  'label',
  'child',
  'mode',
  'acceptedOutcomeCodes',
  'returnCode',
  'cancelCode',
  'failureCode',
  'maximumDepth',
];
export function childWorkFingerprint(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
): string {
  return JSON.stringify([
    document.workflowCode,
    document.definitionVersionCode,
    document.packReference,
    document.scopeTypeCode,
    document.scopeKey,
    document.nodes.map((node) => ({
      id: node.id,
      typeCode: node.typeCode,
      terminalOutcome: node.terminalOutcome,
      ...(node.id === nodeId ? { behavior: node.declaration?.behavior } : {}),
    })),
    nodeId,
  ]);
}
export function terminalOutcomeCode(label: string): string {
  return (
    label
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 120)
      .replace(/-+$/, '') || 'result'
  );
}
export function childRuleError(
  rule: WorkflowStepChildLink,
  choices: readonly StepAuthoringChildDefinition[],
): string | null {
  if (
    !rule ||
    rule.version !== 1 ||
    Object.keys(rule).some((key) => !childRuleFields.includes(key)) ||
    !childCode(rule.id)
  )
    return 'This saved related-work contract is unavailable. Its settings are preserved for inspection.';
  if (
    typeof rule.label !== 'string' ||
    !rule.label.trim() ||
    rule.label !== rule.label.trim() ||
    rule.label.length > 160
  )
    return 'Enter a related-work name of up to 160 characters.';
  if (
    !['blocking', 'observational'].includes(rule.mode) ||
    rule.returnCode !== 'retain_original_visit' ||
    rule.cancelCode !== 'stop_pending_leave_running' ||
    rule.failureCode !== 'blocked' ||
    !Number.isInteger(rule.maximumDepth) ||
    rule.maximumDepth < 1 ||
    rule.maximumDepth > 16
  )
    return 'Select the mode, return, cancellation and failure rules, and a depth limit from 1 to 16.';
  const reference = rule.child;
  if (
    !reference ||
    Object.keys(reference).some(
      (key) =>
        !['workflowCode', 'definitionVersionCode', 'contentHash'].includes(key),
    ) ||
    !childCode(reference.workflowCode) ||
    typeof reference.definitionVersionCode !== 'string' ||
    !reference.definitionVersionCode.trim() ||
    reference.definitionVersionCode !==
      reference.definitionVersionCode.trim() ||
    reference.definitionVersionCode.length > 80 ||
    !/^([a-f0-9]{64})$/.test(reference.contentHash)
  )
    return 'Choose an exact published child workflow version.';
  const matches = choices.filter(
    (choice) =>
      childReferenceIdentity(choice) === childReferenceIdentity(reference),
  );
  if (matches.length !== 1 || !matches[0].availableForNewUse)
    return 'The saved child workflow version is unavailable for new use. Choose a compatible published version explicitly.';
  if (
    !Array.isArray(rule.acceptedOutcomeCodes) ||
    rule.acceptedOutcomeCodes.length < 1 ||
    rule.acceptedOutcomeCodes.length > 50 ||
    new Set(rule.acceptedOutcomeCodes).size !==
      rule.acceptedOutcomeCodes.length ||
    rule.acceptedOutcomeCodes.some(
      (code) =>
        !matches[0].terminalOutcomes.some((outcome) => outcome.code === code),
    )
  )
    return 'Select at least one result from this exact child workflow version.';
  return null;
}
export function applyChildLinks(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  rules: readonly WorkflowStepChildLink[],
  original: readonly WorkflowStepChildLink[],
  choices: readonly StepAuthoringChildDefinition[],
): WorkflowDefinitionDocument {
  const targets = Array.isArray(document.nodes) ? document.nodes.filter(node => node?.id === nodeId) : [];
  if (
    targets.length !== 1 || targets[0].typeCode !== 'human_step' || !Array.isArray(rules) || !Array.isArray(original) ||
    rules.length > 16 ||
    new Set(rules.map((rule) => rule.id)).size !== rules.length
  )
    throw new Error(
      'Select one work step and at most 16 uniquely identified related-work rules.',
    );
  const saved = targets[0].declaration?.behavior?.childLinks ?? [];
  if (!Array.isArray(saved)) throw new Error('Saved related-work settings are unavailable for editing and remain preserved.');
  if (JSON.stringify(rules) === JSON.stringify(saved)) return document;
  for (const rule of rules) {
    const retained = original.filter((item) => item?.id === rule?.id);
    if (retained.length === 1 && JSON.stringify(retained[0]) === JSON.stringify(rule)) continue;
    const error = childRuleError(rule, choices);
    if (error) throw new Error(error);
  }
  const next = initializeBlankStep(
    rules.length
      ? {
          ...document,
          payloadSchemaVersion: requireWorkflowPayloadVersion(
            document.payloadSchemaVersion,
            '1.17',
          ),
        }
      : document,
    nodeId,
  );
  return {
    ...next,
    payloadSchemaVersion: rules.length
      ? requireWorkflowPayloadVersion(next.payloadSchemaVersion, '1.17')
      : next.payloadSchemaVersion,
    nodes: next.nodes.map((node) =>
      node.id !== nodeId
        ? node
        : {
            ...node,
            declaration: {
              ...node.declaration!,
              behavior: {
                ...node.declaration!.behavior,
                childLinks: structuredClone([...rules]),
              },
            },
          },
    ),
  };
}
export function applyTerminalOutcome(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  outcome: WorkflowTerminalOutcome | null,
): WorkflowDefinitionDocument {
  const targets = Array.isArray(document.nodes) ? document.nodes.filter(node => node?.id === nodeId) : [];
  if (
    targets.length !== 1 || targets[0].typeCode !== 'terminal'
  )
    throw new Error('Select one completion point.');
  if (
    outcome &&
    (outcome.version !== 1 ||
      Object.keys(outcome).some(
        (key) => !['version', 'code', 'label'].includes(key),
      ) ||
      !childCode(outcome.code) ||
      typeof outcome.label !== 'string' ||
      !outcome.label.trim() ||
      outcome.label !== outcome.label.trim() ||
      outcome.label.length > 120)
  )
    throw new Error('Enter a workflow result name of up to 120 characters.');
  const saved = targets[0].terminalOutcome;
  if (outcome && saved && outcome.code !== saved.code)
    throw new Error('The saved workflow result code is fixed. Remove its mapping explicitly before defining a different result.');
  if (
    outcome &&
    document.nodes.some(
      (node) =>
        node.id !== nodeId &&
        node.typeCode === 'terminal' &&
        node.terminalOutcome?.code === outcome.code,
    )
  )
    throw new Error('Use a distinct result name for this completion point.');
  if (outcome ? isEqual(outcome, saved) : saved == null) return document;
  return {
    ...document,
    payloadSchemaVersion: outcome
      ? requireWorkflowPayloadVersion(document.payloadSchemaVersion, '1.17')
      : document.payloadSchemaVersion,
    nodes: document.nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const next = { ...node };
      if (outcome) next.terminalOutcome = structuredClone(outcome);
      else delete next.terminalOutcome;
      return next;
    }),
  };
}
